import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useToast } from '../../contexts/ToastContext';
import { integrationApiService, type LightspeedSyncEstimate } from '../../services/integrationApi';
import { InlineMessage, LoadingState, SectionCard } from '../ui';
import type { LightspeedSettingsSectionProps } from './lightspeedSettingsRegistry';

const T = 'integrations.lightspeedConfig.syncEstimate';

/** A full first sync longer than this gets a warning, not just a line of text. */
export const LONG_SYNC_SECONDS = 2 * 60 * 60;

type Unit = 'underMinute' | 'minutes' | 'hours' | 'days';
export const describeSeconds = (seconds: number): { unit: Unit; count: number } => {
  if (seconds < 60) return { unit: 'underMinute', count: 0 };
  if (seconds < 3600) return { unit: 'minutes', count: Math.round(seconds / 60) };
  if (seconds < 86400) return { unit: 'hours', count: Math.round(seconds / 3600) };
  return { unit: 'days', count: Math.round((seconds / 86400) * 10) / 10 };
};

/** "Read 10/22/2026, 9:14 AM": when the account's limit was last read, or '' when it never was. */
const formatReadAt = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
};

/**
 * How long a first sync takes for this store (flag `lightspeed_sync_estimates`): worked out from the
 * rate limit Lightspeed reports for the account and the products selected, for link only, fill gaps
 * and full update, plus what 10 photos per product would add. Reads only; later syncs touch only what changed.
 *
 * "Check my Lightspeed limit again" re-reads the account's limit and says what it found in a toast
 * (the numbers, still not reported, or failed, from the server's `throttleReadStatus`; the server keeps
 * the last reading whenever a read produces none): the estimate often looks the same after a re-read,
 * and a button that changes nothing on screen reads as broken (MOT-590, Tuttle).
 */
const LightspeedSyncEstimateSection: React.FC<LightspeedSettingsSectionProps> = ({ integration }) => {
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const queryKey = ['lightspeed-sync-estimate', integration.id];
  const query = useQuery({
    queryKey,
    queryFn: () => integrationApiService.getLightspeedSyncEstimate(integration.id),
    staleTime: 60_000,
  });
  const recheck = useMutation({
    mutationFn: () => integrationApiService.getLightspeedSyncEstimate(integration.id, true),
    onSuccess: (next: LightspeedSyncEstimate) => {
      // The server keeps the last reading when a re-read produces none, so the new estimate never loses a measured limit.
      queryClient.setQueryData(queryKey, next);
      showToast(...recheckToast(next));
    },
    onError: () => showToast(t(`${T}.recheckFailed`), 'error'),
  });

  /**
   * The toast for a finished recheck, from what the server's read found: a failed read is worth retrying,
   * an account that answered without a limit is not, and only a fresh reading names the numbers (a kept
   * older reading is still `throttleMeasured`, so that flag alone cannot say a read worked).
   */
  const recheckToast = (next: LightspeedSyncEstimate): [string, 'success' | 'warning' | 'error'] => {
    const status = next.throttleReadStatus ?? (next.throttleMeasured ? 'read' : 'not_reported');
    if (status === 'failed') return [t(`${T}.recheckFailed`), 'error'];
    if (status === 'read') {
      return [t(`${T}.recheckRead`, { bucket: next.throttle.bucketSize, drip: next.throttle.dripRate }), 'success'];
    }
    return [t(`${T}.recheckUnknown`), 'warning'];
  };

  const duration = (seconds: number) => {
    const d = describeSeconds(seconds);
    return t(`${T}.duration.${d.unit}`, { count: d.count });
  };

  /** One mode's time: a floor when only the selection size is known, a range when photos may be added to bare items. */
  const modeTime = (mode: { seconds: number; secondsMax: number; atLeast: boolean }) => {
    if (mode.atLeast) return t(`${T}.atLeast`, { time: duration(mode.seconds) });
    const low = duration(mode.seconds);
    const high = duration(mode.secondsMax);
    return low === high ? low : t(`${T}.range`, { time: low, max: high });
  };

  if (query.isLoading) {
    return (
      <SectionCard title={t(`${T}.section`)} dense>
        <LoadingState />
      </SectionCard>
    );
  }
  const estimate = query.data;
  if (query.isError || !estimate) {
    return (
      <SectionCard title={t(`${T}.section`)} dense>
        <Typography variant="body2" color="text.secondary">{t(`${T}.unavailable`)}</Typography>
      </SectionCard>
    );
  }

  const { modes, throttle, photoImpact } = estimate;
  const rows: Array<{ key: 'link_only' | 'enrich' | 'override' }> = [{ key: 'link_only' }, { key: 'enrich' }, { key: 'override' }];
  const long = modes.override.secondsMax > LONG_SYNC_SECONDS;
  const readAt = estimate.throttleMeasured ? formatReadAt(estimate.throttleMeasuredAt) : '';

  return (
    <SectionCard title={t(`${T}.section`)} dense>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="body2">
          {t(`${T}.summary`, { selected: estimate.selected, linked: estimate.linked })}
        </Typography>
        {long && <InlineMessage severity="warning">{t(`${T}.long`, { time: duration(modes.override.secondsMax) })}</InlineMessage>}
        <Box component="ul" sx={{ m: 0, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {rows.map(({ key }) => (
            <Typography component="li" variant="body2" key={key}>
              <strong>{t(`${T}.mode.${key}.label`)}:</strong> {modeTime(modes[key])}. {t(`${T}.mode.${key}.what`)}
            </Typography>
          ))}
        </Box>
        {photoImpact.extraSeconds > 0 && (
          <Typography variant="body2">
            {t(`${T}.photos`, {
              current: photoImpact.current.photos,
              full: photoImpact.full.photos,
              extra: duration(photoImpact.extraSeconds),
            })}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary">
          {estimate.throttleMeasured
            ? t(`${T}.limit`, { bucket: throttle.bucketSize, drip: throttle.dripRate })
            : t(`${T}.limitUnknown`)}
          {readAt ? ` ${t(`${T}.limitReadAt`, { date: readAt })}` : ''}
          {' '}
          {t(`${T}.basis`)}
        </Typography>
        <Box>
          <Button size="small" onClick={() => recheck.mutate()} disabled={recheck.isPending}>
            {recheck.isPending ? t(`${T}.checking`) : t(`${T}.recheck`)}
          </Button>
        </Box>
      </Box>
    </SectionCard>
  );
};

export default LightspeedSyncEstimateSection;
